import {useEffect, useRef, useState} from 'react';
import {mergeAll} from 'rxjs/operators';
import useSocket from 'src/hooks/use-socket';
import useReportActive from './useReportActive';

// Posicao em transito de um device revogado ao vivo (MON-570 removeu do deviceMap antes dessa
// posicao chegar pelo socket) nao pode virar exception -- mesmo guard de useMapTrace(V2).js.
export const syncLabelOnPosition = (map, deviceMap, deviceId, longitude, latitude, addOverlay, updateOverlayPositionByOverlay) => {
  const overlay = map.findOverlayById(`label-${deviceId}`);
  if (overlay) {
    updateOverlayPositionByOverlay(overlay, [longitude, latitude]);
    return;
  }
  const device = deviceMap.get(+deviceId);
  if (!device) return;
  addOverlay(device, [longitude, latitude]);
};

export const removeLabelsForDevices = (map, deviceIds) => {
  if (!Array.isArray(deviceIds)) return;
  deviceIds.forEach((id) => map.removeOverlayById(`label-${id}`));
};

const useMapLabel = (state, dispatch, instance) => {
  const {instance: map, deviceMap} = state;

  const {positions, devicesRemoved: devicesRemovedObservable} = useSocket();
  const unsubscribe$ = useRef(null);
  const [label, setLabel] = useState(false);
  const reportActive = useReportActive();

  const addOverlay = (device, coord) => {
    map.addLabelOverlay(device.id, device.name, coord);
  };

  const updateOverlayPositionByOverlay = (overlay, coord) => {
    map.updateOverlayPosition(null, coord, overlay);
  };

  const removeOverlays = () => {
    map.removeOverlays('label');
  };

  const toggleLabel = () => {
    setLabel((prev) => !prev);
  };

  useEffect(() => {
    if (map && deviceMap) {
      if (label && !reportActive) {
        deviceMap.forEach((device) => {
          if (device.currentPosition) {
            const {longitude, latitude} = device.currentPosition;
            addOverlay(device, [longitude, latitude]);
          }
        });
      } else {
        removeOverlays();
      }
    }
  }, [map, label, deviceMap, reportActive]);

  useEffect(() => {
    if (map && deviceMap) {
      if (label && !reportActive) {
        unsubscribe$.current = positions
            .pipe(mergeAll())
            .subscribe(({deviceId, longitude, latitude}) => {
              syncLabelOnPosition(map, deviceMap, deviceId, longitude, latitude, addOverlay, updateOverlayPositionByOverlay);
            });
      } else {
        if (unsubscribe$.current) {
          unsubscribe$.current.unsubscribe();
        }
      }
    }

    return () => {
      if (unsubscribe$.current) {
        unsubscribe$.current.unsubscribe();
      }
    };
  }, [map, deviceMap, label, reportActive]);

  // MON-570: dispositivo revogado ao vivo tinha seu marcador removido, mas a label continuava
  // presa no mapa pra sempre (nada assinava esse canal aqui) -- reproduzido ao vivo.
  useEffect(() => {
    let devicesRemovedSubscription$;

    if (map) {
      devicesRemovedSubscription$ = devicesRemovedObservable.subscribe((deviceIds) => {
        removeLabelsForDevices(map, deviceIds);
      });
    }

    return () => {
      if (devicesRemovedSubscription$) {
        devicesRemovedSubscription$.unsubscribe();
      }
    };
  }, [map, devicesRemovedObservable]);

  return {label, toggleLabel};
};

export default useMapLabel;
